import {createSlice} from "@reduxjs/toolkit";
import _, {isArray} from "lodash";
import moment from 'moment';
import API from "../../app/api";
import config from "../../app/config";
import Formatter from '../../util/formatter';
import Notes from '../../util/notes';
import {setOpenDischargeTimestampInfoForm, setSnackbar, setTempPiw} from './globals';
import {addNoteForPatient} from './notes';
import {setLoading as setLoadingGlobal} from "./patient";
import {hasPermission, Permissions, sendUserAudit} from './user';
import {getWardsIdForPatientsSearch} from './wardMap';
import { AUDIT_TYPES } from "../../util/auditUtil";

export const wardSlice = createSlice({
    name: "ward",
    initialState: {
        wards: [],
        onlyActiveSingleWards: [],
        value: undefined,
        loading: undefined,
        loadingPatients: false,
        loadingWards: false,
        updating: false,
        patients: undefined,
        statuses: undefined,
        bedasList: {},
        singleBedasSessions: {},
        barsList: {},
        singleBarsSessions: {},
        updateCounter: 0,
        connectionError: false,
        currentMap: null,
        mapHospitalList: [],
        isMultiHospitalMode: false,
        currentHospital: null,
        cleaningRequestState: null,
        cleaningRequestObject: null,
    },
    reducers: {
        setWard: (state, ward) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.value = ward.payload;
        },
        setStatuses: (state, statuses) => {
            state.statuses = statuses.payload;
        },
        setWardPatients: (state, wardPatients) => {
            let wardPatientsNew = wardPatients.payload;
            if (wardPatientsNew && isArray(wardPatientsNew)) {
                wardPatientsNew.forEach((wardPatient) => {
                    wardPatient.haveSameName = _.filter(wardPatientsNew, (wp) => {
                        return _.toLower(wardPatient?.patient?.familyName) === _.toLower(wp?.patient?.familyName)
                    }).length > 1;
                })
            }
            state.patients = wardPatientsNew;
        },
        reloadFromServer: state => {
            // TODO: reloadFromServer
        },
        setBedasList: (state, bedas) => {
            state.bedasList = bedas.payload;
        },
        setSingleBedasSessions: (state, bedas) => {
            state.singleBedasSessions = bedas.payload;
        },
        setBarsList: (state, bedas) => {
            state.barsList = bedas.payload;
        },
        setSingleBarsSessions: (state, bedas) => {
            state.singleBarsSessions = bedas.payload;
        },
        setLoading: (state, wardsId) => {
            state.loading = wardsId.payload;
        },
        setUpdating: (state, wardsId) => {
            state.updating = wardsId.payload;
        },
        setConnectionError: (state, isError) => {
            state.connectionError = isError.payload;
        },
        incUpdateCounter: (state) => {
            state.updateCounter = state.updateCounter + 1;
        },
        setCurrentMap: (state, ward) => {
            state.currentMap = ward.payload;
        },
        setLoadingPatients: (state, ward) => {
            state.loadingPatients = ward.payload;
        },
        setWards: (state, ward) => {
            state.wards = ward.payload;
        },
        setOnlyActiveSingleWards: (state, ward) => {
            state.onlyActiveSingleWards = ward.payload;
        },
        setLoadingWards: (state, ward) => {
            state.loadingWards = ward.payload;
        },
        setMultiHospitalMode: (state, ward) => {
            state.isMultiHospitalMode = ward.payload;
        },
        setMapHospitalList: (state, ward) => {
            state.mapHospitalList = ward.payload;
        },
        setCurrentHospital: (state, ward) => {
            state.currentHospital = ward.payload;
        },
        setCleaningRequestState: (state, ward) => {
            state.cleaningRequestState = ward.payload;
        },
        setCleaningRequestObject: (state, ward) => {
            state.cleaningRequestObject = ward.payload;
        },
    },
});

export const ward = (state) => state.ward.value;
export const wardsList = (state) => state.ward.wards;
export const onlyActiveSingleWards = (state) =>
  state.ward.onlyActiveSingleWards;

export const getBedasList = (wardId, isBars = false) => (state) =>  {
    const hospitalId = state.ward.currentHospital;
    const combinedId = `${wardId}##${hospitalId}`;
    const key = isBars ? 'barsList' : 'bedasList';

    return state.ward?.statuses?.[combinedId]?.[key]?.[wardId] ||
      state.ward?.[key]?.[wardId] ||
      [];
};

export const getSingleBedasSessions = (wardId, isBars = false) => (state) => {
    const hospitalId = state.ward.currentHospital;
    const combinedId = `${wardId}##${hospitalId}`;
    const key = isBars ? 'singleBarsSessions' : 'singleBedasSessions';

    return state.ward?.statuses?.[combinedId]?.[key]?.[wardId] ||
      state.ward?.[key]?.[wardId] ||
      [];
};

export const statuses = state => state.ward.statuses;
export const loading = state => state.ward.loading;
export const loadingPatients = state => state.ward.loadingPatients;
export const loadingWards = state => state.ward.loadingWards;
export const wardPatients = state => state.ward.patients;
export const wardUpdateCounter = state => state.ward.updateCounter;
export const connectionError = state => state.ward.connectionError;
export const currentMap = state => state.ward.currentMap;
export const multiHospitalMode = state => state.ward.isMultiHospitalMode;
export const mapHospitalList = state => state.ward.mapHospitalList;
export const currentHospital = state => state.ward.currentHospital;
export const cleaningRequestState = state => state.ward.cleaningRequestState;
export const cleaningRequestObject = state => state.ward.cleaningRequestObject;

export const {
    setWard,
    setWards,
    setOnlyActiveSingleWards,
    setWardPatients,
    setBedasList,
    setBarsList,
    setSingleBedasSessions,
    setSingleBarsSessions,
    setLoading,
    setLoadingPatients,
    setLoadingWards,
    setUpdating,
    incUpdateCounter,
    setStatuses,
    reloadFromServer,
    setConnectionError,
    setCurrentMap,
    setMultiHospitalMode,
    setCurrentHospital,
    setMapHospitalList,
    setCleaningRequestState,
    setCleaningRequestObject,
} = wardSlice.actions;

const updateDataOnServer = (dispatch, patientId, data, dataDate) => {
    dispatch(setUpdating(true));
    return new Promise(async function (resolve, reject) {
        API.updatePatientInWard(patientId, data, dataDate).then(response => {
            if (response.data === true) {
                dispatch(setUpdating(false));
                resolve(true);
            } else {
                dispatch(setUpdating(false));
                // FIXME: some error message !
                console.error("error in network", response);
                // reject(false);
                resolve(false);
            }
        });
    });
};

export const loadWardList = () => async (dispatch, getState) => {
    if (getState().ward.loadingWards === false) {
        dispatch(setLoadingWards(true));
        API.getWardMapList(null)
            .then(response => {
                if (response?.status === 200 && response?.data) {
                    const maps = _.filter(response?.data?.__data, map => !map.archived);
                    dispatch(setWards(maps || []));
                    const filtered = _.filter(maps, w => w.active && w.wardsId.length === 1);
                    dispatch(setOnlyActiveSingleWards(filtered));
                } else if (response?.status === 204) {
                    dispatch(setWards([]));
                    dispatch(setOnlyActiveSingleWards([]));
                    console.warn("Ward list is empty");
                } else {
                    console.error("ERROR Loading wards", response);
                    dispatch(setSnackbar({
                        type: "error",
                        message: "There was a problem while downloading the wards list.",
                    }));
                }
            })
            .catch(err => {
                console.error("ERROR Loading wards", err);
                dispatch(setSnackbar({
                    type: "error",
                    message: "There was a problem while downloading the wards list.",
                }));
            })
            .finally(() => {
                dispatch(setLoadingWards(false));
            });
    }
};

export const updatePatientBed = (patientId, bedId) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);

    let dischargeTimeMarker = false;
    if (config?.dischargeTimestampList && config?.dischargeTimestampList?.includes(bedId)) {
        dischargeTimeMarker = true;
    }

    if (config?.waitingListLimits && !_.isNil(config?.waitingListLimits[bedId])) {
        const limit = config?.waitingListLimits[bedId];
        const ptsInList = _.filter(patientsClone, {bed: bedId});

        if (ptsInList?.length >= limit) {
            dispatch(setSnackbar({type: 'warning', message: 'The list has reached the limit of patients.'}));
            return ;
        }
    }

    for (let patient of patientsClone) {
        if (patient.__id === patientId) {
            const dataToUpdate = [];
            const oldBed = patient?.bed || 'wlist1';

            if (oldBed !== bedId) {
                // If patient was moved from discharge list then remove timestamp
                if (config?.dischargeTimestampList && config?.dischargeTimestampList?.includes(oldBed)) {
                    dataToUpdate.push({'metadata.dischargeTimeMarker': null});
                    if (config?.dischargeTimestampAdditionalInfo) {
                        dataToUpdate.push({'metadata.dischargeTimestampData': null});
                        if (patient?.metadata?.dischargeTimestampData) {
                            dispatch(addNoteForPatient(patient.patient.__id, {
                                note: Notes.getDischargeTimestampListNote(null, true),
                                noteType: 'dischargeTimestamp',
                                patientsInWardsRef: [patient.__id],
                                episodeId: patient?.admission?.consultantEpisodeId || null,
                            }, patient.__id));
                        }
                    }
                }

                // If patient was moved to discharge list then add timestamp
                if (dischargeTimeMarker) {
                    dataToUpdate.push({'metadata.dischargeTimeMarker': moment().toISOString()});
                    if (config?.dischargeTimestampAdditionalInfo && patient?.studies?.discharge?.status !== 'timestamp') {
                        dispatch(setOpenDischargeTimestampInfoForm(true));
                        dispatch(setTempPiw({id: patient.__id, oldBed: oldBed}));
                    }
                }

                addBedHistory(dataToUpdate, bedId, patient);
            }

            patient.bed = bedId;
            dataToUpdate.push({"bed": bedId});
            ambulanceArrival(dataToUpdate, bedId, patient, getState().dicts.ecds.arrivalMode);

            // we need to update this data ...
            const status = await updateDataOnServer(dispatch, patient.__id, dataToUpdate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
                if (config?.enableLocationNote) {
                    if (!_.isEmpty(getState()?.dicts?.beds)) {
                        const oldBedObj = _.find(getState()?.dicts?.beds, {__id: oldBed});
                        const newBedObj = _.find(getState()?.dicts?.beds, {__id: bedId});

                        const note = `Location changed from ${getLocation(oldBed, oldBedObj)} to ${getLocation(bedId, newBedObj)}`;
                        API.addClinicalNote(patient?.patient?.__id, {
                            note: note,
                            patientsInWardsRef: [patient?.__id],
                            noteType: 'Location',
                            episodeId: patient?.admission?.consultantEpisodeId || null,
                        }).then(() => {
                            dispatch(setSnackbar({type: 'success', message: 'Location note saved.'}));
                        }).catch(error => {
                            console.error(error);
                            dispatch(setSnackbar({type: 'error', message: 'Note not saved.'}));
                        });
                    } else {
                        console.warn('Beds dictionary is empty!');
                    }
                }
                return true;
            } else {
                return false;
            }
        }
    }
};

export const updatePatientList = (patientId, listId, boardId) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);

    for (let patient of patientsClone) {
        if (patient.__id === patientId) {
            const dataToUpdate = [];

            if(patient.metadata.listIds) {
                patient.metadata.listIds[boardId] = listId;
            } else {
                patient.metadata.listIds = {
                    [boardId]: listId
                }
            }

            dataToUpdate.push({["metadata.listIds."+boardId]: listId});

            // we need to update this data ...
            const status = await updateDataOnServer(dispatch, patient.__id, dataToUpdate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
                return true;
            } else {
                return false;
            }
        }
    }
};


const getLocation = (bedId, bedObj) => {
    if (bedObj) {
        if (_.startsWith(bedId, 'wlist')) return bedObj?.overrideLocation ? bedObj?.overrideLocation : bedObj?.location; // TODO CHECK WARD

        return `${bedObj?.overrideLocation ? bedObj?.overrideLocation : bedObj?.location} Bed ${bedObj?.__description}`;
    }
    console.error(`${bedId} is not in Bed Dictionary`);
    return bedId;
};

const addBedHistory = (dataToUpdate, bed, patient) => {
    const array = patient?.metadata?.bedHistory ? _.cloneDeep(patient?.metadata?.bedHistory) : [];
    if (_.isNull(bed)) {
        array.push({bed: null, when: moment().toISOString()});
    } else if (_.isString(bed)) {
        array.push({bed, when: moment().toISOString()});
    } else if (_.isObject(bed)) {
        array.push({...bed, when: moment().toISOString()});
    }
    _.set(patient, 'metadata.bedHistory', array);
    dataToUpdate.push({'metadata.bedHistory': array});
};

const ambulanceArrival = (dataToUpdate, bed, patient, arrivalMods) => {
    const bedsExcludes = ['wlist1', 'rbhplaster', 'wlist11', 'wlist12rbh', 'wlist5', 'wlist9rbh', 'wlist7rbh', 'wlist4', 'wlist9', 'Relatives'];
    let ambulance = [];
    _.forEach(_.filter(arrivalMods, {group: 'ambulance'}), element => {
        ambulance.push(element.__code);
    });

    if (bed && !bedsExcludes.includes(bed) && _.isNil(patient?.metadata?.ambulanceArrival) && ambulance.includes(patient?.admission?.arrivalModeCode)) {
        dataToUpdate.push({'metadata.ambulanceArrival': moment().toISOString()});
    }
};

// TODO temporary method. Delete this later
export const updateMap = (map) => (dispatch, getState) => {
    if (config.modules?.map?.customMap) {
        dispatch(setCurrentMap(map))
    }
};

export const updatePatientBedNew = (patientId, body) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    for (let patient of patientsClone) {
        if (patient.__id === patientId) {
            let dataToUpdate, bed;
            const oldBed = patient?.bedDescription || '-';
            const oldBedId = patient?.bed || 'Patient Lobby';
            let backToLobby = false;

            if (body) {
                patient.bed = body.bedId;
                patient.bedDescription = body?.bed?.name;
                patient.area = body?.areaId;
                patient.areaDescription = body?.area?.name;

                dataToUpdate = [
                    {bed: body.bedId},
                    {bedDescription: body?.bed?.name},
                    {area: body?.areaId},
                    {areaDescription: body?.area?.name},
                ];

                bed = {
                    bed: body.bedId,
                    bedDescription: body?.bed?.name,
                    area: body?.areaId,
                    areaDescription: body?.area?.name,
                };
            } else {
                patient.bed = null;
                patient.bedDescription = null;
                patient.area = null;
                patient.areaDescription = null;
                backToLobby = true;

                dataToUpdate = [
                    {bed: null},
                    {bedDescription: null},
                    {area: null},
                    {areaDescription: null},
                ];
                bed = null;
            }

            addBedHistory(dataToUpdate, bed, patient);

            // we need to update this data ...
            const status = await updateDataOnServer(dispatch, patient.__id, dataToUpdate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
                let newBedText = body?.bed?.name || body?.bedId;
                if (backToLobby) {
                    newBedText = 'Patient Lobby';
                }

                const note = `Location changed from ${oldBed || oldBedId} to ${newBedText}`;
                API.addClinicalNote(patient?.patient?.__id, {
                    note: note,
                    patientsInWardsRef: [patient?.__id],
                    noteType: 'Location',
                    episodeId: patient?.admission?.consultantEpisodeId || null,
                }).then(() => {
                    dispatch(setSnackbar({type: 'success', message: 'Location note saved.'}));
                }).catch(error => {
                    console.error(error);
                    dispatch(setSnackbar({type: 'error', message: 'Note not saved.'}));
                });
                return true;
            } else {
                return false;
            }
        }
    }
};

export const updateBedasStatus = (hospitalId, data, afterSaveEvent, hospital, mode = 'create') => async (dispatch, getState) => {
    const isDelete = mode === 'delete';
    const isUpdate = mode === 'update';

    const sliceKey = data?.isBars ? 'barsList' : 'bedasList';
    const bedasSliceState = getState().ward?.[sliceKey];

    let bedasClone = _.cloneDeep(getState().ward?.[sliceKey]?.[hospital?.hospitalId] || []);

    if (isDelete) {
        _.remove(bedasClone, {sessionId: data.sessionId});
    } else if (isUpdate) {
        const index = _.findIndex(bedasClone, {sessionId: data.sessionId});
        if (index === -1) {
            dispatch(setSnackbar({type: "error", message: "Follow Up Session not saved. Not found session with given ID!"}));
            return;
        } else {
            bedasClone[index] = data;
        }
    } else {
        bedasClone.push(data);
    }

    if (!_.isEmpty(bedasClone)) {
        bedasClone = _.sortBy(bedasClone, ['startDate']);
    }

    const combinedState = {
        ...bedasSliceState,
        [hospital?.hospitalId]: bedasClone,
        modifiedDate: moment().toISOString(),
    }

    let ward = 'ED';
    // if (hospitalId) ward += encodeURIComponent(`##${hospitalId}`); // For UHD
    if (hospitalId) ward += `##${hospitalId}`; // For UHD

    try {
        const response = await API.updateWardData(ward, [{[sliceKey]: combinedState}]);

        if (response.data) {
            const setter = data?.isBars ? setBarsList : setBedasList;
            dispatch(setSnackbar({type: "success", message: "Follow Up Session was successfully saved."}));
            dispatch(setter(combinedState));
        }
    } catch (e) {
        dispatch(setSnackbar({type: "error", message: "Follow Up Session not saved. Error occured!"}));
        console.error("error in network", e);
    } finally {
        if (afterSaveEvent) {
            afterSaveEvent();
        }
    }
}

export const updateSingleBedasSessions = (hospitalId, data, afterSaveEvent, hospital) => async (dispatch, getState) => {
  const sliceKey = data?.isBars ? 'singleBarsSessions' : 'singleBedasSessions';

    let sessionsClone = _.cloneDeep(data);
    let sessionsFromSlice = _.cloneDeep(getState().ward?.[sliceKey]?.[hospital?.hospitalId] || []);

    // Look if there is any session in data array and iterate through our slice state,
    // get this item and remove because this object in data object will overwrite current state with newer state
    if (!_.isEmpty(sessionsFromSlice)) {
        _.forEach(data, (sess) => {
            const sliceIndex = sessionsFromSlice.findIndex(item => (item?.from === sess?.from) && item?.startDate === sess?.startDate);
            if (sliceIndex >= 0 ) {
                sessionsFromSlice.splice(sliceIndex, 1);
            }
        })
    }

    sessionsClone = [...sessionsClone, ...sessionsFromSlice];

    // Sort events by start date and remove older than today
    if (!_.isEmpty(sessionsClone)) {
        sessionsClone = _.sortBy(sessionsClone, ['from']);
        sessionsClone = _.filter(sessionsClone, session => moment(session?.from || session?.startDate).isSameOrAfter(moment().set({hours: 0, minutes: 0, seconds: 0, milliseconds: 0})));
    }

    const combinedSessions = {
        ...getState().ward?.[sliceKey],
        [hospital?.hospitalId]: sessionsClone,
        modifiedDate: moment().toISOString(),
    }

    let ward = 'ED';
    if (hospitalId) ward += `##${hospitalId}`; // For UHD

    try {
        const response = await API.updateWardData(ward, [{[sliceKey]: combinedSessions}]);
        if (response.data) {
            const setter = data?.isBars ? setSingleBarsSessions : setSingleBedasSessions;
            dispatch(setSnackbar({type: "success", message: "Follow Up Session was successfully saved."}));
            dispatch(setter(combinedSessions)
            );
        }
    } catch (e) {
        dispatch(setSnackbar({type: "error", message: "Follow Up Session not saved. Error occurred!"}));
        console.error("error in network", e);
    } finally {
        if (afterSaveEvent) {
            afterSaveEvent();
        }
    }
}

export const getSingleSessionsByDay = (day, hospitalId, isBars = false) => {
    const sliceKey = isBars ? 'singleBarsSessions' : 'singleBedasSessions';
    return (dispatch, getState) => {
        const sessions = getState().ward?.[sliceKey]?.[hospitalId];
        const sessionsByDay = _.filter(sessions, (session) => {
            return moment(session.from || session?.startDate).isSame(day, 'day');
        });

        return sessionsByDay;
    }
}

// TODO After release WardMapCreator delete oldMap param
export const updatePatientBedStatus = (bed, status) => async (dispatch, getState) => {
    let statusesDataClone = _.cloneDeep(getState().ward.statuses);
    let response;
    if (_.isString(bed)) {
        if (statusesDataClone.bedStatuses == null) {
            statusesDataClone.bedStatuses = {};
        }
        if (status === "unlock") {
            delete statusesDataClone.bedStatuses[bed];
        } else {
            statusesDataClone.bedStatuses[bed] = status;
        }

        let ward = statusesDataClone.wardId;
        // For UHD bed status refresh
        // if (getState()?.ward?.currentMap?.hospitalId) {
        //     ward = encodeURIComponent(`ED##${getState()?.ward?.currentMap?.hospitalId}`);
        // }

        response = await API.updateWardData(ward, [ {"bedStatuses": statusesDataClone.bedStatuses}]);
    } else {
        try {
            let wardId = bed?.wardId;
            const bedId = bed?.__id;

            if (getState().ward.isMultiHospitalMode && wardId === 'ED') {
                const hospital = getState().ward.currentHospital;
                wardId = `${wardId}##${hospital}`;
            }

            if (statusesDataClone[wardId]?.bedStatuses == null) {
                statusesDataClone[wardId].bedStatuses = {};
            }

            if (status === "unlock") {
                delete statusesDataClone[wardId].bedStatuses[bedId];
            } else {
                statusesDataClone[wardId].bedStatuses[bedId] = status;
            }

            response = await API.updateWardData(wardId, [{"bedStatuses": statusesDataClone[wardId].bedStatuses}]);
        } catch (e) {
            console.error(e);
            dispatch(setSnackbar({type: "error", message: "Bed status was not saved successfully!"}));
        }
    }
    if (response.data) {
        dispatch(setStatuses(statusesDataClone));
        dispatch(setSnackbar({type: "success", message: "Bed status was saved successfully."}));
    } else {
        dispatch(setSnackbar({type: "error", message: "Bed status was not saved successfully!"}));
        console.error("error in network", response);
    }
};

export const updatePatientStatuses = (icon, patient, type) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    for (let dataPatient of patientsClone) {
        if (dataPatient.__id === patient.__id) {
            if (!dataPatient.flags) {
                dataPatient.flags = [];
            }
            let founded = false;
            for (let f of dataPatient.flags) {
                if (f.name === icon.name) {
                    founded = true;
                    if (f.value === 1) {
                        if (dispatch(hasPermission(Permissions.AGYLE_CAN_REMOVE_TASKS))) {
                            f.value--;
                        } else {
                            dispatch(setSnackbar({type: "warning", message: "User has no permission to delete task."}));
                        }
                    } else {
                        f.value--;
                    }
                }
            }
            if (!founded) {
                let obj = {
                    name: icon.name,
                    value: icon.statusesCount - 1,
                }
                if (type !== "task") obj["type"] = type
                dataPatient.flags.push(obj);
            }
            _.remove(dataPatient.flags, v => v.value <= 0);

            const status = await updateDataOnServer(dispatch, dataPatient.__id, [{"flags": dataPatient.flags}]);
            // const status = true;
            if (status) {
                dispatch(setWardPatients(patientsClone));
            }
        }
    }
};

export const updatePatientsData = (updates, afterSaveEvent) => async (dispatch, getState) => {
    API.updatePatientsInWard(updates).then(response => {
        // set patients data in parent records !
        if (response.data === true) {
            afterSaveEvent(true);

            const patientsClone = _.cloneDeep(getState().ward.patients);
            for (let patientCloned of patientsClone) {
                for (let update of updates) {
                    if (update.patientInWardId === patientCloned.__id) {
                        // apply all updates
                        _.forEach(update.set, (s) => {
                            _.forEach(s, (s, name) => {
                                _.set(patientCloned, name, s);
                            });
                        });
                    }
                }
            }

            dispatch(setWardPatients(patientsClone));
        } else {
            afterSaveEvent(false);
        }
    }).catch(error => {
        console.error(error);
        dispatch(setSnackbar({type: "error", message: "There was a problem with saving data."}));
    });
};

export const updatePatientData = (patient, data, afterSaveEvent, dataDate) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    for (let patientCloned of patientsClone) {
        if (patientCloned.__id === patient.__id) {
            _.forEach(data, d => {
                _.forEach(d, (d, n) => {
                    _.set(patientCloned, n, d);
                });
            });

            const status = await updateDataOnServer(dispatch, patient.__id, data, dataDate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
            }
            if (afterSaveEvent) {
                afterSaveEvent(status);
            }
            break;
        }
    }
};

// TODO After release WardMapCreator delete oldMap param
export const updateWardPatientsByWardId = (wardIdArray, oldMap, signal) => (dispatch, getState) => {
    if (getState().ward.loading === undefined && !getState().ward.updating && !getState().ward.loadingPatients) {
        dispatch(setLoading(wardIdArray));

        const patientsInfo = [];
        for (let patientInWard of getState().ward.patients) {
            if (patientInWard?.__id) { // form remove null as piw id
                patientsInfo.push([patientInWard.__id, patientInWard.objectVersion]);
            }
        }

        dispatch(setUpdating(true));
        API.getWardsAttendancesUpdates(wardIdArray, patientsInfo, signal).then(changes => {
            const patientsClone = getState().ward.patients ? _.cloneDeep(getState().ward.patients) : [];
            let updatedStatus = false;
            if (!_.isEmpty(changes?.deleted)) {
                updatedStatus = true;
                _.remove(patientsClone, pat => {
                    let patDeleted = false;
                    for (let deletedId of changes.deleted) {
                        if (deletedId === pat.__id) {
                            patDeleted = true;
                            break;
                        }
                    }
                    return patDeleted;
                });
            }
            if (!_.isEmpty(changes?.added)) {
                updatedStatus = true;
                for (let patientToAdd of changes.added) {
                    patientsClone.push(patientToAdd);
                }
            }
            if (!_.isEmpty(changes?.updated)) {
                updatedStatus = true;
                for (let patientUpdate of changes.updated) {
                    _.forEach(patientsClone, pat => {
                        if (pat.__id === patientUpdate.__id) {
                            // _.merge(pat, patientUpdate);
                            _.assign(pat, patientUpdate);
                        }
                    });
                }
            }
            if (updatedStatus) {
                dispatch(setWardPatients(patientsClone));
            }

            let type = 'ED';
            if (!_.isEmpty(wardIdArray) && wardIdArray[0] && _.startsWith(wardIdArray[0], 'ED##')) {
                type = wardIdArray[0];
            }

            // TODO After release WardMapCreator delete this
            if (oldMap && changes && changes.statuses && changes.statuses[type]) {
                const newStatuses = changes.statuses[type];
                const oldStatuses = _.cloneDeep(getState().ward.statuses);
                const areSame = _.isEqual(newStatuses?.bedStatuses, oldStatuses?.bedStatuses) &&
                  _.isEqual(newStatuses?.bedasList, oldStatuses?.bedasList) &&
                  _.isEqual(newStatuses?.barsList, oldStatuses?.barsList) &&
                  _.isEqual(newStatuses?.singleBedasSessions, oldStatuses?.singleBedasSessions) &&
                  _.isEqual(newStatuses?.singleBarsSessions, oldStatuses?.singleBarsSessions);
                if (!areSame) {
                    // compare them first !!!!
                    dispatch(setStatuses(changes.statuses[type]));
                }
            }

            if (!oldMap) {
                let multiWards = {};
                let areSameStatuses = true;
                const oldStatuses = _.cloneDeep(getState().ward.statuses);

                _.forEach(changes.statuses, stats => {
                    multiWards = {
                        ...multiWards,
                        [stats.wardId]: stats,
                        modifiedDate: moment().toISOString(),
                    }

                    if (areSameStatuses) areSameStatuses = _.isEqual(oldStatuses?.[stats.wardId]?.bedStatuses, stats?.bedStatuses) &&
                      _.isEqual(oldStatuses?.[stats.wardId]?.bedasList, stats?.bedasList) &&
                      _.isEqual(oldStatuses?.[stats.wardId]?.barsList, stats?.barsList) &&
                      _.isEqual(oldStatuses?.[stats.wardId]?.singleBedasSessions, stats?.singleBedasSessions) &&
                      _.isEqual(oldStatuses?.[stats.wardId]?.singleBarsSessions, stats?.singleBarsSessions);
                    ;
                });

                if (!areSameStatuses) {
                    dispatch(setStatuses(multiWards));
                }
            }

            dispatch(setConnectionError(false));
            dispatch(setLoading(undefined));
        }).catch(error => {
            console.error(error);
            dispatch(setConnectionError(true));
            // we do need to set communication error flag !
            // there need to be some sort of handling for that !

            dispatch(setLoading(undefined));
            // dispatch(incUpdateCounter());
            // FIXME: error handler
            // ...
        }).finally(() => dispatch(setUpdating(false)))
    }
};

// TODO After release WardMapCreator delete oldMap param
// TODO if failed then repeat
export const loadWardPatientsByWardId = (wardIdArray, withoutDischarged, oldMap) => async (dispatch, getState) => {
    if (getState().ward.loading !== wardIdArray) {
        dispatch(setWardPatients([]));
        dispatch(setLoadingGlobal(true));
        dispatch(setLoading(wardIdArray));
        dispatch(setLoadingPatients(true));
        API.getWardsAttendances(wardIdArray).then(r => {
            if (withoutDischarged) {
                const ptArr = [];

                _.forEach(r, pt => {
                    if (pt) {
                        let elementHidden = false;
                        if (_.startsWith(pt?.bed, 'wlist') || !pt?.bed) { // TODO CHECK WARD
                            if (pt?.admission?.locationDescription === 'DISCHARGE') {
                                elementHidden = true;
                            }
                        }

                        if (!elementHidden) ptArr.push(pt);
                    }
                });
                dispatch(setWardPatients(ptArr));
            } else {
                dispatch(setWardPatients(r));
            }
        }).finally(() => dispatch(setLoadingPatients(false)))

        let subFolder = "";
        if (config.modules?.map?.customMap && getState().ward.currentMap) {
            subFolder = `${getState().ward.currentMap.mapPath}/`;
        }

        // TODO After release WardMapCreator delete this
        let wardData = {
            _id: "ed",
            name: "ED",
            // mapName: '/maps/ed.svg',
            mapName: `/maps/${subFolder}`,
            // patients: [
            //     patientOnWard
            // ],
            settings: {
                // FIXME: ....
            },
            userSettings: {
                // FIXME: ....
            },
        };
        dispatch(setWard(wardData));

        // TODO After release WardMapCreator delete this
        if (oldMap) {
            let ward = 'ED';
            // For UHD bed status refresh
            if (!_.isEmpty(wardIdArray) && wardIdArray[0] && _.startsWith(wardIdArray[0], 'ED##')) {
                ward = wardIdArray[0];
            }
            API.getWardData(ward).then(stat => {
                dispatch(setStatuses(stat));
                if (stat?.bedasList) {
                    dispatch(setBedasList(stat?.bedasList));
                }
                if (stat?.singleBedasSessions) {
                    dispatch(setSingleBedasSessions(stat?.singleBedasSessions));
                }
                if (stat?.barsList) {
                    dispatch(setBarsList(stat?.barsList));
                }
                if (stat?.singleBarsSessions) {
                    dispatch(setSingleBarsSessions(stat?.singleBarsSessions));
                }
            });
        } else {
            let multiWards = {};

            for (const arr of wardIdArray) {
                let id = arr;
                const multiHospital = _.startsWith(id, 'ED##');
                // if (multiHospital) id = encodeURIComponent(id);
                await API.getWardData(id)
                    .then(stats => {
                        multiWards = {
                            ...multiWards,
                            [stats.wardId]: stats,
                        };
                        if (multiHospital) {
                            multiWards = {
                                ...multiWards,
                                [stats.wardId?.replace('ED##', '')]: stats,
                            };
                        }
                    });
            }
            dispatch(setStatuses(multiWards));
        }
        dispatch(setLoadingGlobal(false));
        dispatch(setLoading(undefined));
    }
};

export const saveCleaningRequest = (data) => async (dispatch, getState) => {
    if (!data?.wardId || !data?.cleaningRequest) {
        console.error('Wrong data structure.');
        return null;
    }
    const userObject = getState().user.userData;
    const userFullName = Formatter.formatUserFullName(userObject);

    return await API.saveCleaningRequest(data)
        .then(res => {
            const auditData = {
                cleaningId: data.cleaningRequest.__id,
                user: userFullName,
                login: userObject?.userName,
                cleaningRequest: data.cleaningRequest,
            };

            dispatch(setSnackbar({
                type: 'success',
                message: 'Bed cleaning request was successfully saved.',
            }));

            dispatch(sendUserAudit(null, AUDIT_TYPES.CLEANING_REQUEST, auditData));

            return res;
        })
        .catch(error => {
            console.error(error);
            dispatch(setSnackbar({
                type: 'error',
                message: 'There was a problem when saving the cleaning bed request.',
            }));
        });
};

/**
 * Bed cleaning request function
 *
 * If a map has a custom cleaning option, it will be used, otherwise the global settings will be used.
 *
 * @param {string} bed - Bed id form DB
 * @param {string} type - Type of action
 */
export const cleaningRequestPath = (bed, type) => async (dispatch, getState) => {
    if (config?.modules?.cleaningRequest?.enabled && bed && bed?.length > 10) { // Length check for ObjectId (new map bed)
        const conf = getState().externalConfig.cleaningRequestCfg;
        const customMapOpt = getState().wardMap?.customCleaningMapOption;
        // Get a bed object
        const bedObj = await API.getBed(bed);
        // If the bed exists
        if (bedObj) {
            if (customMapOpt) { // Check that the cleaning option is enabled for this type of bed
                if (bedObj?.forCleaning) {
                    const getModeType = customMapOpt?.[type] || 'manual';
                    switch (getModeType) {
                        case 'auto':
                            dispatch(autoCleaningRequest(bedObj));
                            break;
                        case 'prompt':
                            dispatch(setCleaningRequestState(bedObj));
                            break;
                        case 'manual':
                        default:
                            return;
                    }
                }
            } else if (conf?.triggers?.[type]) { // Check if cleaning is enabled for the specific type
                // Check if the bed is not a list
                if (bedObj?.type !== 'wlist') {
                    const isED = bedObj?.wardId === 'ED';
                    const location = conf?.location?.[isED ? 'ED' : 'IP'];
                    // Check if cleaning is enabled for the specific ward type
                    if (location?.enabled) {
                        if (location?.auto) {
                            // Submit an automated request
                            dispatch(autoCleaningRequest(bedObj));
                        } else {
                            // Show request popup
                            dispatch(setCleaningRequestState(bedObj));
                        }
                    }
                }
            }
        } else {
            console.error(`For bed id [${bed}] cannot load object from DB.`);
            dispatch(setSnackbar({
                type: 'error',
                message: 'There was a problem when saving the cleaning bed request.',
            }));
        }
    }
};

/**
 * Function to automatically request bed cleaning
 * @param {Object} bed - Bed object
 */
const autoCleaningRequest = (bed) => (dispatch, getState) => {
    const conf = getState().externalConfig.cleaningRequestCfg;
    const findManagementType = _.find(conf?.wards, w => w.hospitalId === bed?.hospitalId && w.wardId === bed?.wardId);
    const getEstimatedTime = conf?.levels?.level1;
    const wardIds = dispatch(getWardsIdForPatientsSearch([bed?.wardId]));

    const userObject = getState().user.userData;
    const userFullName = Formatter.formatUserFullName(userObject);

    let data = {
        wardId: wardIds[0],
        cleaningRequest: {
            bedId: bed?.__id,
            level: '1',
            description: 'Auto Request',
            status: 'OPEN',
            managementType: findManagementType?.option || 'internal',
            wardId: bed?.wardId,
            hospitalId: bed?.hospitalId,
            expectedDate: moment().add(getEstimatedTime || 0, 'minute').toISOString(),
            createdByUserName: userFullName,
        },
    };
    dispatch(saveCleaningRequest(data));
};

export default wardSlice.reducer;
